import { Component, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { getAutoStartEnabled, isDefined, isValidTargetRect, setAutoStartEnabled } from "../utils";
import { GuidedTourStep } from "../types";
import { GuidedTourBackdrop } from "./guided-tour-backdrop";
import { GuidedTourProgress } from "./guided-tour-progress";
import { debounce } from "../../../utils/debounce";
import { ButtonSize, ButtonVariant } from "../../button/button.enum";

const baseClass = "ux-guided-tour-overlay";

@Component({
    tag: "ux-guided-tour-overlay",
    styleUrl: "guided-tour-overlay.scss",
    shadow: false
})
export class GuidedTourOverlay {
    private canvasElement: HTMLCanvasElement;
    private focusElement: HTMLElement;
    private popoverElement: HTMLUxPopoverElement;
    private bodyElement: HTMLParagraphElement;

    /**
     * Unique hash of the tour
     */
    @Prop() readonly tourHash: string = "";

    /**
     * Bounding rect of the target element
     */
    @Prop() readonly targetRect: DOMRect | null = null;

    /**
     * Offset between target rect and focus rect
     */
    @Prop() readonly targetOffset: number = 20;

    /**
     * The current guided-tour step data
     */
    // @ts-expect-error Check for typing
    @Prop() readonly step: GuidedTourStep = null;

    /**
     * Index of the step in the steps array
     */
    @Prop() readonly stepIndex: number = 0;

    /**
     * How many steps are available
     */
    @Prop() readonly totalSteps: number = 0;

    /**
     * Display autoStart switch in popover
     */
    @Prop() readonly autoStartSwitch: boolean = false;

    /**
     * Label for the autoStart switch
     */
    @Prop() readonly autoStartLabel: string = "Show on startup";

    /**
     * Label for the 'finish tour' button – will always be added to the last step
     */
    @Prop() readonly finishTourButtonLabel?: string;

    /**
     * If true, the overlay is faded in
     */
    @State() isReady = false;

    /**
     * Is the popover currently opened or not
     */
    @State() popoverOpened = false;

    @Listen("resize", { target: "window" })
    handleResize() {
        this.drawCanvasDebounced();
    }

    @Watch("targetOffset")
    @Watch("targetRect")
    onTargetRectChange() {
        this.drawCanvas();
    }

    @Watch("step")
    onStepChange() {
        this.closePopover();
        this.drawCanvas();
        if (isDefined(this.step)) {
            this.updateBody();
            this.openPopover();
        }
    }

    private openPopover = debounce(() => {
        if (isDefined(this.step)) {
            this.popoverOpened = true;
            this.focusNextButton();
        }
    }, 100);

    private closePopover = () => {
        this.popoverOpened = false;
    };

    private focusNextButton = debounce(() => {
        const nextButton: HTMLButtonElement = this.popoverElement.querySelector(
            '[data-trigger-guided-tour="next"]'
        ) as HTMLButtonElement;
        if (nextButton !== null) {
            nextButton.focus();
        }
    }, 100);

    private drawCanvasDebounced = debounce(() => this.drawCanvas(), 10);

    private drawCanvas = () => {
        const { step, canvasElement, targetRect } = this;
        if (canvasElement === null) {
            return;
        }
        canvasElement.width = window.innerWidth;
        canvasElement.height = window.innerHeight;
        const ctx = canvasElement.getContext("2d");
        if (ctx === null) {
            return;
        }
        ctx.beginPath();
        ctx.rect(0, 0, canvasElement.width, canvasElement.height);
        if (!targetRect) {
            return;
        }
        if (isValidTargetRect(targetRect)) {
            const targetOffset = isDefined(step) && isDefined(step.targetOffset) ? step.targetOffset : this.targetOffset;
            const hole = {
                top: targetRect.top - (targetOffset ?? 0),
                bottom: targetRect.top + targetRect.height + (targetOffset ?? 0),
                left: targetRect.left - (targetOffset ?? 0),
                right: targetRect.left + targetRect.width + (targetOffset ?? 0)
            };
            ctx.moveTo(hole.left, hole.top);
            ctx.lineTo(hole.left, hole.bottom);
            ctx.lineTo(hole.right, hole.bottom);
            ctx.lineTo(hole.right, hole.top);
            ctx.lineTo(hole.left, hole.top);
        }

        ctx.closePath();
        ctx.fillStyle = "#000";
        ctx.fill();
    };

    private updateBody = () => {
        if (this.bodyElement === null) {
            return;
        }
        if (isDefined(this.step)) {
            this.bodyElement.innerHTML = this.step.body || "";
        } else {
            this.bodyElement.innerHTML = "";
        }
    };

    componentWillLoad() {
        this.onStepChange();
    }

    componentDidLoad() {
        this.drawCanvas();
        this.updateBody();
        if (!this.isReady) {
            window.setTimeout(() => {
                this.isReady = true;
            }, 100);
        }
    }

    componentDidRender() {
        this.popoverElement.updatePlacement();
    }

    render() {
        const { step, stepIndex, autoStartSwitch, autoStartLabel, finishTourButtonLabel, popoverOpened, targetRect, totalSteps } =
            this;
        // @ts-expect-error Check for typing
        const hasTarget = isValidTargetRect(targetRect);
        const hasStep = isDefined(step);
        const hasNextButtonText = hasStep && typeof step.nextButtonLabel === "string";
        const hasBackButtonText = hasStep && typeof step.backButtonLabel === "string";
        const targetOffset = hasStep && isDefined(step.targetOffset) ? step.targetOffset : this.targetOffset;
        const focusStyle = hasTarget
            ? {
                  // @ts-expect-error Check for typing
                  top: `${targetRect.top - targetOffset}px`,
                  // @ts-expect-error Check for typing
                  left: `${targetRect.left - targetOffset}px`,
                  // @ts-expect-error Check for typing
                  width: `${targetRect.width + 2 * targetOffset}px`,
                  // @ts-expect-error Check for typing
                  height: `${targetRect.height + 2 * targetOffset}px`
              }
            : undefined;
        const focusElement = hasTarget ? this.focusElement : null;
        const nextButtonCustomClassNames = hasStep ? this.step.nextButtonClassNames || "" : "";
        const backButtonCustomClassNames = hasStep ? this.step.backButtonClassNames || "" : "";
        // @ts-expect-error Check for typing
        const focusFx: string = hasStep ? this.step.focusFx : "glow";
        const hideStepsCount = hasStep ? this.step.hideStepsCount : false;
        const hideProgressBar = hasStep ? this.step.hideProgressBar : false;
        const isStartScreen = stepIndex === -1;
        const isLastStep = stepIndex + 1 === totalSteps;
        const hasFinishButtonLabel = finishTourButtonLabel !== undefined;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--ready`]: this.isReady,
                    [`${baseClass}--active`]: this.isReady && hasStep
                }}
            >
                <GuidedTourBackdrop targetRect={targetRect} />
                {/* @ts-expect-error Check for typing */}
                <canvas class={`${baseClass}__canvas`} ref={(el) => (this.canvasElement = el)} />
                <div
                    class={{
                        [`${baseClass}__focus`]: true,
                        [`${baseClass}__focus--highlight`]: focusFx !== "shadow",
                        [`${baseClass}__focus--shadow`]: focusFx === "shadow"
                    }}
                    style={focusStyle}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.focusElement = el)}
                />
                <ux-popover
                    // @ts-expect-error Check for typing
                    targetElement={focusElement}
                    controlled={true}
                    opened={popoverOpened}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.popoverElement = el)}
                    placement={focusElement !== null ? step?.placement || "center" : "center"}
                >
                    <ux-popover-panel>
                        <button class="ux-guided-tour__close-button" data-trigger-guided-tour="stop">
                            <ux-icon name="cross" weight="normal" />
                        </button>
                        {totalSteps > 0 && stepIndex >= 0 && (
                            <div
                                class="ux-guided-tour-overlay__steps"
                                style={{
                                    display: hideStepsCount ? "none" : "block"
                                }}
                                aria-hidden={hideStepsCount ? "true" : undefined}
                            >
                                {stepIndex + 1} / {totalSteps}
                            </div>
                        )}
                        <h4 class="ux-guided-tour__title ux-text ux-text-title--bold">{step?.title}</h4>
                        {/* @ts-expect-error Check for typing */}
                        <p class="ux-guided-tour__body ux-text ux-text--normal" ref={(el) => (this.bodyElement = el)} />
                        {autoStartSwitch && (
                            <ux-switch-input
                                name="settings"
                                label={autoStartLabel}
                                desc=""
                                checked={getAutoStartEnabled(this.tourHash)}
                                onUxChange={(event) => setAutoStartEnabled(event.detail, this.tourHash)}
                            />
                        )}
                        <div class="ux-guided-tour-overlay__buttons">
                            {(stepIndex > 0 || hasBackButtonText) && (
                                <ux-button
                                    class={{
                                        [backButtonCustomClassNames]: backButtonCustomClassNames !== ""
                                    }}
                                    data-trigger-guided-tour={stepIndex > 0 ? "prev" : "stop"}
                                    size={!hasBackButtonText ? ButtonSize.Icon : ButtonSize.Default}
                                    {...(hasBackButtonText && { label: step.backButtonLabel })}
                                    {...(!hasBackButtonText && { icon: "chevron-left" })}
                                ></ux-button>
                            )}
                            <ux-button
                                class={{
                                    [nextButtonCustomClassNames]: nextButtonCustomClassNames !== ""
                                }}
                                data-trigger-guided-tour={isLastStep ? "finish" : "next"}
                                variant={
                                    isStartScreen || (isLastStep && hasFinishButtonLabel)
                                        ? ButtonVariant.Primary
                                        : ButtonVariant.Default
                                }
                                size={
                                    (!isLastStep && !hasNextButtonText) ||
                                    (isLastStep && !hasNextButtonText && !hasFinishButtonLabel)
                                        ? ButtonSize.Icon
                                        : ButtonSize.Default
                                }
                                label={
                                    hasFinishButtonLabel && isLastStep
                                        ? finishTourButtonLabel
                                        : hasNextButtonText
                                          ? step.nextButtonLabel
                                          : ""
                                }
                                {...(!hasNextButtonText && { icon: "chevron-right" })}
                            ></ux-button>
                        </div>
                        {!hideProgressBar && <GuidedTourProgress total={totalSteps} step={stepIndex + 1} />}
                    </ux-popover-panel>
                </ux-popover>
            </Host>
        );
    }
}
